/**
 * @author Claude Code
 * @description Patient Context Payers Dropdown - Shows patient's insurance coverages with tags and rich information
 */
import React, { useMemo } from 'react';
import clsx from 'clsx';
import _ from 'lodash';
import { Select, Tag, Typography, Flex } from 'antd';
import { getParsedCoverageInfo, getCategorizedInsurance } from 'utils/patients/insurance';
import { PAYER_RESPONSIBILITY_OPTIONS } from 'app-constants/claims/claimRequest';

export interface IPatientCoveragePayersProps {
	value?: string;
	onChange?: (value: string) => void;
	patientCoverages: Insurance.ICoverageResponseModel[];
	metadata: Metadata.IResponse;
	placeholder?: string;
}

/**
 * Patient Context Payers Dropdown
 * Shows patient's insurance coverages grouped by eligibility availability.
 * Unmapped payers (no tenantPayer.payerId) go to the "not eligible" group.
 */
const PatientCoveragePayers: React.FC<IPatientCoveragePayersProps> = ({
	value,
	onChange,
	patientCoverages = [],
	metadata,
	placeholder = 'Select a payer',
}) => {
	// options use coverageId as value for uniqueness — the field value is coverageId (not tenantPayerId)
	const options = useMemo(() => {
		if (_.isEmpty(patientCoverages)) {
			return [];
		}

		const { currentInsurance, oldInsurance } = getCategorizedInsurance(patientCoverages);

		const mapCoverageToOption = (coverage: Insurance.ICoverageResponseModel, isArchived: boolean) => {
			const parsed = getParsedCoverageInfo(coverage);
			const coverageId = coverage.id || '';
			const tenantPayerId = _.split(coverage?.fhirResource?.insurer?.reference, '/')?.[1];
			const internalPayerId = coverage?.metadata?.internalPayerId as string;

			// Resolve display name: tenant payer name → registered payer name → FHIR payer name
			const tenantPayer = tenantPayerId ? _.find(metadata?.tenantPayers, { id: tenantPayerId }) : undefined;
			const registeredPayer = internalPayerId ? _.get(metadata?.allPayersMapping, internalPayerId) : undefined;
			const displayName = tenantPayer?.name || registeredPayer?.name || parsed.payerInfo.payerName || '';

			const memberId = parsed?.subscriberInfo?.memberId;
			const insuranceTypeCode = parsed?.insurancePlanInfo?.insuranceTypeCode;
			const responsibilityCode = parsed?.extensionMeta?.responsibilitySequence;
			const responsibilityLabel = _.find(PAYER_RESPONSIBILITY_OPTIONS, { value: responsibilityCode })?.label;

			// A payer is available for eligibility only if:
			// 1. It is mapped (tenantPayer.payerId is set)
			// 2. It exists in selectedActiveEligibilityPayers
			const isAvailable = !!tenantPayer && !!_.get(metadata.selectedActiveEligibilityPayers, tenantPayer?.id);

			return {
				value: coverageId,
				label: (
					<Flex vertical gap={4}>
						<Flex align='center' gap={8} justify='space-between'>
							<span style={{ fontWeight: 500 }}>{displayName || 'Unknown Payer'}</span>
							<Tag color={isArchived ? 'default' : 'success'} className={clsx('m-0', { 'opacity-70': !isAvailable })}>
								{isArchived ? 'Archived' : 'Current'}
							</Tag>
						</Flex>
						<Typography.Text type='secondary' style={{ fontSize: 12 }}>
							{[
								`Member ID: ${memberId ?? '-'}`,
								`Insurance Type: ${insuranceTypeCode ?? '-'}`,
								`Responsibility: ${responsibilityLabel ?? '-'}`,
							].join(' | ')}
						</Typography.Text>
					</Flex>
				),
				searchLabel: displayName || 'Unknown Payer',
				isAvailable,
			};
		};

		const currentOptions = _.map(currentInsurance, (coverage) => mapCoverageToOption(coverage, false));
		const archivedOptions = _.map(oldInsurance, (coverage) => mapCoverageToOption(coverage, true));
		const allOptions = [...currentOptions, ...archivedOptions];

		const availableOptions = [
			..._.filter(currentOptions, (option) => option.isAvailable),
			..._.filter(archivedOptions, (option) => option.isAvailable),
		].map((o) => _.omit(o, 'isAvailable'));

		const unavailableOptions = _.filter(allOptions, (option) => !option.isAvailable).map((option) => ({
			..._.omit(option, 'isAvailable'),
			disabled: true,
		}));

		return _.isEmpty(unavailableOptions)
			? availableOptions
			: [
					...availableOptions,
					{
						label: (
							<Typography.Text className='text-xs' type='secondary'>
								{'Payers not available for Eligibility'}
							</Typography.Text>
						),
						options: unavailableOptions,
					},
				];
	}, [patientCoverages, metadata?.tenantPayers, metadata.allPayersMapping, metadata.selectedActiveEligibilityPayers]);

	const handleChange = (coverageId: string) => {
		onChange?.(coverageId);
	};

	return (
		<Select
			value={value || undefined}
			onChange={handleChange}
			options={options}
			placeholder={placeholder}
			showSearch
			optionLabelProp='searchLabel'
			style={{ width: '100%' }}
		/>
	);
};

export default PatientCoveragePayers;
